import { Controller, useFormContext } from 'react-hook-form';
import { clsx } from 'clsx';
import Input from 'src/components/Input/Input';
import { getSubdomainHelper } from 'src/helpers/getSubdomainHelper';
import { ZENDESK_HELP_CENTER_URL } from 'src/constants/urls';
import { Button } from 'src/components/ui/button';
import { Collapsible } from 'src/components/ui/collapsible';
import { Separator } from 'src/components/ui/separator';
import { Spinner } from 'src/components/Spinner/Spinner';
import ArrowLargeIcon from 'src/assets/img/arrow-large-icon.svg';
import ChevronDownIcon from 'src/assets/img/chevron-down-icon.svg';
import ChevronDownDisabledIcon from 'src/assets/img/chevron-down-disabled-icon.svg';

export const EditZendeskEmailApiDetailsSettings = ({
  isAuthorized,
  isZendeskOAuthUsed,
  isManualConnectActive,
  isCheckingAuthStatus,
  onManualConnectOpenChange,
  onConnectWithZendesk,
  onReconnectWithZendesk,
}: {
  isAuthorized: boolean;
  isZendeskOAuthUsed: boolean;
  isManualConnectActive: boolean;
  isCheckingAuthStatus: boolean;
  onManualConnectOpenChange: (open: boolean) => void;
  onConnectWithZendesk: () => void;
  onReconnectWithZendesk: () => void;
}) => {
  const { control } = useFormContext();
  return (
    <div className={'w-full flex flex-col gap-2.5 p-2.5 bg-[#A498FF1A] rounded-sm-5'}>
      {isCheckingAuthStatus && (
        <div
          className={
            'absolute flex items-center justify-center w-full h-full top-0 left-0 bg-ebony_opacity_30 backdrop-blur-sm z-50'
          }
        >
          <div
            className={
              'flex flex-col items-center gap-4 bg-light p-4 rounded-sm-5 text-sm font-semibold'
            }
          >
            <Spinner className={'!w-4 !border-[3px] !border-r-ebony_opacity_50'} />
            <p>Checking Zendesk Connection...</p>
          </div>
        </div>
      )}
      <div>
        <Controller
          control={control}
          name="subdomain"
          render={({ field: { onChange, value }, fieldState: { error } }) => (
            <Input
              type={'text'}
              label={'Zendesk subdomain'}
              placeholder={'domain.com'}
              invalidMessage={error?.message}
              value={value}
              isValid={!error?.message}
              onChange={(e) => {
                const res = getSubdomainHelper(e.target.value);
                if (res?.subdomain) {
                  onChange(res?.subdomain);
                } else {
                  onChange(e.target.value);
                }
              }}
              infoTooltip={{
                side: 'right',
                description: 'Link to your Zendesk workspace.',
              }}
            />
          )}
        />
      </div>
      <div className={'mt-2.5 flex flex-col gap-2'}>
        <Button
          type={'button'}
          size={'sm'}
          onClick={onConnectWithZendesk}
          disabled={isManualConnectActive || (isZendeskOAuthUsed && isAuthorized)}
          className={clsx(
            'w-full gap-2 px-8',
            isZendeskOAuthUsed && isAuthorized
              ? 'bg-purple_150 text-purple_600 font-medium shadow-none disabled:opacity-100'
              : 'font-semibold bg-ebony text-white hover:bg-ebony_opacity_80 shadow-[0_1px_1px_0_rgba(5,5,13,0.25)]',
          )}
        >
          {isAuthorized && isZendeskOAuthUsed ? (
            'Connected successfully with Zendesk'
          ) : (
            <>
              {'Connect with Zendesk'}
              <img src={ArrowLargeIcon} alt={''} width={16} height={16} />
            </>
          )}
        </Button>
        {isZendeskOAuthUsed && isAuthorized && (
          <Button
            type={'button'}
            size={'sm'}
            onClick={onReconnectWithZendesk}
            className={
              'w-full gap-2 px-8 font-semibold bg-ebony text-white hover:bg-ebony_opacity_80 shadow-[0_1px_1px_0_rgba(5,5,13,0.25)]'
            }
          >
            {'Reconnect with Zendesk'}
            <img src={ArrowLargeIcon} alt={''} width={16} height={16} />
          </Button>
        )}
        <p className={'text-center text-[13px] font-medium text-tertiary'}>
          You’ll be redirected to Zendesk to authorize access.
        </p>
        <div className={'flex items-center gap-2'}>
          <Separator className={'flex-1 bg-border_subtle'} />
          <span className={'text-[13px] font-medium text-tertiary'}>or</span>
          <Separator className={'flex-1 bg-border_subtle'} />
        </div>
        <Collapsible
          open={isManualConnectActive}
          onOpenChange={onManualConnectOpenChange}
          disabled={isZendeskOAuthUsed}
          triggerClassName={
            'group flex w-full items-center gap-2 rounded-lg border border-grey_border bg-white px-3 py-2 text-sm font-medium text-grey_750 transition-colors enabled:hover:border-purple_600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple_600/20 data-[state=open]:border-purple_600 disabled:cursor-not-allowed disabled:border-border_subtle disabled:text-grey_300'
          }
          trigger={
            <>
              <span className={'flex-1 text-center'}>Connect manually with API token</span>
              {isZendeskOAuthUsed ? (
                <img src={ChevronDownDisabledIcon} alt={''} width={16} height={16} />
              ) : (
                <img
                  src={ChevronDownIcon}
                  alt={''}
                  width={12.8}
                  height={12.8}
                  className={'transition-transform duration-200 group-data-[state=open]:rotate-180'}
                />
              )}
            </>
          }
          contentClassName={
            'overflow-hidden data-[state=open]:animate-collapsible-down data-[state=closed]:animate-collapsible-up'
          }
        >
          <div className={'flex flex-col gap-2.5 pt-2'}>
            <div>
              <Controller
                control={control}
                name="api_key"
                render={({ field: { onChange, value }, fieldState: { error } }) => (
                  <Input
                    type={'text'}
                    label={'API token'}
                    placeholder={'Yb2KQfS8VQj1HxZlOzVbXk4P6W1Cm3aA1dFxGnYq'}
                    invalidMessage={error?.message}
                    value={value}
                    isValid={!error?.message}
                    onChange={onChange}
                    infoTooltip={{
                      side: 'right',
                      description: (
                        <p>
                          Check how to find the API Key{' '}
                          <a
                            className={'underline'}
                            href={ZENDESK_HELP_CENTER_URL.API_TOKEN}
                            target={'_blank'}
                            rel={'noopener noreferrer'}
                          >
                            here →
                          </a>
                        </p>
                      ),
                    }}
                  />
                )}
              />
            </div>
            <div>
              <Controller
                control={control}
                name="email"
                render={({ field: { onChange, value }, fieldState: { error } }) => (
                  <Input
                    type={'text'}
                    label={'Admin email address'}
                    placeholder={'email@domain.com'}
                    invalidMessage={error?.message}
                    value={value}
                    isValid={!error?.message}
                    onChange={onChange}
                    infoTooltip={{
                      side: 'right',
                      description: 'Email address of Zendesk user used to create API Token',
                    }}
                  />
                )}
              />
            </div>
          </div>
        </Collapsible>
      </div>
    </div>
  );
};
